import { useCallback, useState } from "react";
import { Alert } from "react-native";
import { mixpanel } from "../../../../../../utils/mixpanel";
import {
  VoiceNote,
  Patient,
  UseNoteEditorReturn,
  SignedNote,
} from "../types/VoiceNotes";
import { TSession } from "../../../../../../utils/types";

export const useNoteEditor = (
  patient: Patient,
  session: TSession,
): UseNoteEditorReturn => {
  const [editingField, setEditingField] = useState<
    "transcription" | "summary" | null
  >(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editedSummary, setEditedSummary] = useState<string>("");
  const [expandedSummaries, setExpandedSummaries] = useState<
    Record<string, boolean>
  >({});
  const [signedNotes, setSignedNotes] = useState<Record<string, SignedNote>>(
    {},
  );

  const handleEditSummary = (note: VoiceNote): void => {
    // Don't allow editing if the note is signed
    if (note.is_signed || signedNotes[note.id]?.isSigned) {
      Alert.alert(
        "Note Signed",
        "This note has been signed and cannot be edited.",
      );
      return;
    }

    // Track edit summary start
    mixpanel.track("Voice Note Summary Edit Started", {
      "User Email": session?.userEmail,
      "Clinic Name": session?.clinicName,
      "Patient ID": patient.clinicOSUUID,
      "Patient Name": patient.name,
      "Note ID": note.id,
    });

    // Start editing mode
    setEditingNoteId(note.id);
    setEditingField("summary");
    setEditedSummary(note.note);

    // Ensure the summary is expanded when editing
    setExpandedSummaries((prev) => ({
      ...prev,
      [note.id]: true,
    }));
  };

  const handleCancelEdit = (): void => {
    // Track edit cancelled
    mixpanel.track(
      `Voice Note ${editingField === "summary" ? "Summary" : "Transcript"} Edit Cancelled`,
      {
        "User Email": session?.userEmail,
        "Clinic Name": session?.clinicName,
        "Patient ID": patient.clinicOSUUID,
        "Patient Name": patient.name,
        "Note ID": editingNoteId,
      },
    );

    // Exit editing mode
    setEditingNoteId(null);
    setEditingField(null);
    setEditedSummary("");
  };

  const toggleSummary = (noteId: string): void => {
    setExpandedSummaries((prev) => ({
      ...prev,
      [noteId]: !prev[noteId],
    }));

    // Track summary toggle
    mixpanel.track("Voice Note Summary Toggled", {
      "User Email": session?.userEmail,
      "Clinic Name": session?.clinicName,
      "Patient ID": patient.clinicOSUUID,
      "Patient Name": patient.name,
      Action: expandedSummaries[noteId] ? "Collapse" : "Expand",
    });
  };

  const ensureSummaryExpanded = useCallback((noteId: string): void => {
    setExpandedSummaries((prev) => {
      if (prev[noteId]) {
        return prev;
      }

      return {
        ...prev,
        [noteId]: true,
      };
    });
  }, []);

  return {
    editingField,
    editingNoteId,
    editedSummary,
    expandedSummaries,
    signedNotes,
    handleEditSummary,
    handleCancelEdit,
    toggleSummary,
    ensureSummaryExpanded,
    setEditedSummary,
    setSignedNotes,
  };
};
